// File: src/Subprogram/Login.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Instagram, Send, Phone, ArrowRight } from 'lucide-react';

const API_BASE = 'https://free3dmax.ir/api';

const Login = ({ isOpen, onClose, onLoginSuccess }) => {
  // به جای یک boolean، از یک متغیر متنی برای مدیریت 3 حالت استفاده می‌کنیم
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setLoading(true);

    const action = mode === 'forgot' ? 'forgot_password' : (mode === 'login' ? 'login' : 'signup');
    const payload = {
      action,
      email,
      ...(mode !== 'forgot' && { password }),
      ...(action === 'signup' && { full_name: fullName })
    };

    try {
      const response = await axios.post(`${API_BASE}/users.php`, payload);

      if (response.data.success) {
        if (mode === 'login') {
          onLoginSuccess(response.data.user);
          onClose();
        } else if (mode === 'signup') {
          setMode('login');
          setSuccessMsg('ثبت نام موفقیت‌آمیز بود. لطفاً وارد شوید.');
          setFullName('');
          setPassword('');
        } else if (mode === 'forgot') {
          setMode('login');
          setSuccessMsg(response.data.message || 'رمز عبور جدید به ایمیل شما ارسال شد.');
          setPassword('');
        }
      } else {
        setError(response.data.error || 'عملیات با خطا مواجه شد');
      }
    } catch (err) {
      console.error(err);
      setError('خطا در برقراری ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  const isLoginMode = mode === 'login';
  const isForgotMode = mode === 'forgot';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Main Card */}
      <div className="relative z-10 scale-100 sm:scale-110">
        <style>{`
          /* Custom variables for theme color */
          :root {
            --primary-color: #2563eb; /* Blue-600 matching your site */
            --primary-dark: #1e40af;
            --card-bg: white;
          }

          .login-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            background-color: var(--card-bg);
            width: 17em;
            /* Height adjusts based on mode */
            height: ${mode === 'signup' ? '29em' : (isForgotMode ? '21em' : '24em')}; 
            border-radius: 1.5em;
            box-shadow: 0px 20px 40px rgba(0,0,0,0.2);
            overflow: hidden;
            position: relative;
            transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            font-family: 'Vazirmatn', sans-serif;
          }

          .input-area {
            width: 100%;
            padding: 0 10%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            position: relative;
            z-index: 2;
            transition: all 0.3s ease;
            margin-top: 0.8em;
          }

          .input-field {
            width: 100%;
            border: 2px solid var(--primary-color);
            border-radius: 0.8em;
            height: 3em;
            padding: 0 1em;
            font-size: 0.9em;
            font-weight: 500;
            outline: none;
            transition: all 0.3s ease;
            background: transparent;
            text-align: right;
            direction: ltr; /* Force LTR for typing email/pass correctly */
          }
          
          .input-field::placeholder {
            color: var(--primary-color);
            opacity: 0.7;
            font-weight: bold;
            text-align: right;
          }

          /* Hover Effects Logic */
          .input-area:hover .input-field, .input-field:focus {
            background-color: var(--primary-color);
            color: white;
            border-color: white;
          }
          
          .input-area:hover .input-field::placeholder, .input-field:focus::placeholder {
             color: white;
             opacity: 1;
          }

          /* Background Blob Animation */
          #background-blob {
            width: 100%;
            height: 4em;
            background-color: var(--primary-color);
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
            transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
            box-shadow: inset 0px -5px 10px rgba(0,0,0,0.1);
          }

          .login-header {
             width: 100%;
             height: 4em;
             display: flex;
             justify-content: center;
             align-items: center;
             position: relative;
             z-index: 2;
          }
          
          .card-footer {
             margin-top: auto;
             width: 100%;
             padding: 1em 10% 1.5em;
             display: flex;
             flex-direction: column;
             align-items: center;
             z-index: 2;
             position: relative;
          }

          .action-btn {
             width: 100%;
             background-color: var(--primary-color);
             color: white;
             border: 2px solid var(--primary-color);
             border-radius: 0.8em;
             height: 3em;
             font-weight: bold;
             cursor: pointer;
             transition: all 0.3s ease;
             display: flex;
             align-items: center;
             justify-content: center;
          }

          .card-footer:hover .action-btn {
             background-color: white;
             color: var(--primary-color);
          }
          
          /* Moving the Blob based on Hover */
          /* Name Area (Only in Signup) */
          .login-card:has(#name-input:hover) #background-blob,
          .login-card:has(#name-input:focus) #background-blob {
             height: 6em;
             transform: translateY(4em);
          }

          /* Email Area */
          .login-card:has(#email-input:hover) #background-blob,
          .login-card:has(#email-input:focus) #background-blob {
             height: 6em;
             transform: translateY(${mode === 'signup' ? '7.5em' : '3.5em'});
          }

          /* Password Area (Hidden in Forgot mode) */
          .login-card:has(#password-input:hover) #background-blob,
          .login-card:has(#password-input:focus) #background-blob {
             height: 6em;
             transform: translateY(${mode === 'signup' ? '11.5em' : '8em'});
          }

           /* Footer Area */
          .login-card:has(.card-footer:hover) #background-blob {
             height: 100%;
             transform: translateY(0);
             border-radius: 1.5em;
             opacity: 0.1;
          }

          /* White Filter (Top Corner) - Retained from your original code */
          .white-filter {
            width: 3.5em;
            height: 3.5em;
            top: 0;
            right: 0;
            position: absolute;
            z-index: 5;
            background: transparent;
            border-bottom-left-radius: 1.5em;
            box-shadow: 10px 10px 0px 10px transparent; 
          }
          
          .corner-cut {
             position: absolute;
             top: 0;
             right: 0;
             width: 4em;
             height: 4em;
             background: transparent;
             z-index: 20;
          }
          
          .social-circle {
             display: flex;
             justify-content: center;
             gap: 15px;
             margin-top: 10px;
             padding-bottom: 10px;
             width: 100%;
          }
          .social-icon {
             color: var(--primary-color);
             transition: transform 0.2s;
             cursor: pointer;
          }
          .social-icon:hover {
             transform: scale(1.2);
          }

        `}</style>

        <form className="login-card" onSubmit={handleSubmit}>
          
          {/* Header Title */}
          <div className="login-header text-white">
            {isForgotMode && (
                <button 
                  type="button" 
                  onClick={() => {setMode('login'); setError(null); setSuccessMsg(null);}}
                  className="absolute left-4 top-4 text-white/80 hover:text-white z-50 transition-colors"
                  title="بازگشت به صفحه ورود"
                >
                  <ArrowRight size={20} />
                </button>
            )}
            <h2 className="text-xl font-black tracking-wider">
                {isForgotMode ? 'RECOVERY' : (isLoginMode ? 'LOGIN' : 'SIGN UP')}
            </h2>
            <button 
                type="button" 
                onClick={onClose}
                className="absolute right-4 top-4 text-white/80 hover:text-white z-50 transition-colors"
            >
                <X size={20} />
            </button>
          </div>
          
          <div id="background-blob"></div>

          {/* Form Content */}
          <div className="w-full flex flex-col pt-2 relative z-10">
            
            <p className="text-center text-blue-600 font-bold text-xs mb-2 opacity-80" style={{color: 'var(--primary-color)'}}>
               {isForgotMode ? 'بازیابی رمز عبور' : (isLoginMode ? 'ورود به حساب کاربری' : 'ساخت حساب جدید')}
            </p>

            {/* Error Message */}
            {error && (
                <div className="mx-6 mb-2 p-2 bg-red-50 text-red-500 text-[10px] rounded text-center border border-red-100 font-bold">
                    {error}
                </div>
            )}
            {/* Success Message (برای پیام ارسال ایمیل) */}
            {successMsg && (
                <div className="mx-6 mb-2 p-2 bg-green-50 text-green-600 text-[10px] rounded text-center border border-green-100 font-bold">
                    {successMsg}
                </div>
            )}

            {/* Name Field (Signup Only) */}
            {mode === 'signup' && (
                <div className="input-area">
                    <input 
                        id="name-input"
                        className="input-field" 
                        type="text" 
                        placeholder="نام و نام خانوادگی" 
                        value={fullName}
                        onChange={e => setFullName(e.target.value)}
                        required
                    />
                </div>
            )}

            {/* Email Field */}
            <div className="input-area">
                <input 
                    id="email-input"
                    className="input-field" 
                    type="email" 
                    placeholder="example@mail.com" 
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                />
            </div>

            {/* Password Field (Hidden in Forgot Mode) */}
            {!isForgotMode && (
                <div className="input-area">
                    <input 
                        id="password-input"
                        className="input-field" 
                        type="password" 
                        placeholder="••••••••" 
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                    />
                    {isLoginMode && (
                        <button 
                            type="button"
                            onClick={() => { setMode('forgot'); setError(null); setSuccessMsg(null); }}
                            className="text-[10px] font-bold mt-2 text-left px-1 opacity-70 hover:opacity-100 transition-opacity" 
                            style={{color: 'var(--primary-color)'}}
                        >
                            رمز عبور را فراموش کردید؟
                        </button>
                    )}
                </div>
            )}

          </div>

          {/* Footer Area */}
          <div className="card-footer">
            <button className="action-btn" type="submit" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" /> : (isForgotMode ? 'ارسال رمز جدید' : (isLoginMode ? 'ورود' : 'ثبت نام'))}
            </button>
            
            {!isForgotMode && (
                <div className="flex items-center gap-1 mt-3 text-xs font-bold" style={{color: 'var(--primary-color)'}}>
                <span>{isLoginMode ? 'حساب ندارید؟' : 'حساب دارید؟'}</span>
                <button 
                    type="button"
                    onClick={() => { setMode(isLoginMode ? 'signup' : 'login'); setError(null); setSuccessMsg(null); }}
                    className="underline hover:text-slate-800 transition-colors"
                >
                    {isLoginMode ? 'ثبت نام' : 'ورود'}
                </button>
                </div>
            )}
            
            {/* Social Icons (Adapted from design) */}
            <div className={`social-circle border-t border-slate-100 pt-3 w-full ${isForgotMode ? 'mt-4' : 'mt-2'}`}>
               <a href="#" className="social-icon" title="Instagram"><Instagram size={20} /></a>
               <a href="#" className="social-icon" title="Telegram"><Send size={20} /></a>
               <a href="#" className="social-icon" title="Support"><Phone size={20} /></a>
            </div>
          </div>
          
        </form>
      </div>
    </div>
  );
};

export default Login;